import { lazy, Suspense, useEffect, type ReactElement } from 'react';
import { AppShell } from './components/AppShell.js';
import {
  SkeletonCards,
  SkeletonKanban,
  SkeletonLeadList,
  SkeletonTable,
} from './components/Skeleton.js';
import { DashboardHome } from './modules/dashboard/DashboardHome.js';
import { OnboardingTour } from './components/OnboardingTour.js';
import { ShortcutsRoot } from './components/ShortcutsRoot.js';
import { NPSBanner } from './components/NPSBanner.js';
import { ToastProvider } from './components/ui/index.js';
import { FeatureFlagsProvider, useKillswitch, useFeatureFlag } from './lib/feature-flags.js';
import { useAuth } from './lib/auth.js';
import { canAccessWebsite } from './lib/website-access.js';
import { usePathname } from './lib/router.js';
import { initNativePush } from './lib/native.js';

// Code-splitting · cada espacio en su chunk (PERF-002)
const LeadBoard = lazy(() =>
  import('./modules/crm/LeadBoard.js').then((m) => ({ default: m.LeadBoard })),
);
const ServiceCatalogManager = lazy(() =>
  import('./modules/crm/ServiceCatalogManager.js').then((m) => ({
    default: m.ServiceCatalogManager,
  })),
);
const LeadArchive = lazy(() =>
  import('./modules/crm/LeadArchive.js').then((m) => ({ default: m.LeadArchive })),
);
const ContactsManager = lazy(() =>
  import('./modules/crm/ContactsManager.js').then((m) => ({ default: m.ContactsManager })),
);
const TrackInboxSpace = lazy(() =>
  import('./modules/track/TrackInboxSpace.js').then((m) => ({ default: m.TrackInboxSpace })),
);
const ContabilidadSpace = lazy(() =>
  import('./modules/contabilidad/ContabilidadSpace.js').then((m) => ({
    default: m.ContabilidadSpace,
  })),
);
const CalendarioSpace = lazy(() =>
  import('./modules/calendario/CalendarioSpace.js').then((m) => ({ default: m.CalendarioSpace })),
);
const ProyectosSpace = lazy(() =>
  import('./modules/proyectos/ProyectosSpace.js').then((m) => ({ default: m.ProyectosSpace })),
);
const WebsiteSpace = lazy(() =>
  import('./modules/website/WebsiteSpace.js').then((m) => ({ default: m.WebsiteSpace })),
);
const AlmacenSpace = lazy(() =>
  import('./modules/almacen/AlmacenSpace.js').then((m) => ({ default: m.AlmacenSpace })),
);
const AdministracionSpace = lazy(() =>
  import('./modules/administracion/AdministracionSpace.js').then((m) => ({
    default: m.AdministracionSpace,
  })),
);
const ChatSidebar = lazy(() =>
  import('./modules/chat/ChatSidebar.js').then((m) => ({ default: m.ChatSidebar })),
);
const CommandPalette = lazy(() =>
  import('./components/CommandPalette.js').then((m) => ({ default: m.CommandPalette })),
);
// Widget flotante de Sugerencias · fuera del entry bundle (presupuesto F2.3)
const FeedbackWidget = lazy(() =>
  import('./components/FeedbackWidget.js').then((m) => ({ default: m.FeedbackWidget })),
);
const FichajeMobilePage = lazy(() =>
  import('./modules/fichaje/FichajeMobilePage.js').then((m) => ({ default: m.FichajeMobilePage })),
);
// Rutas de acceso ocultas/dedicadas · fuera del entry bundle (presupuesto F2.3)
const DevLogin = lazy(() =>
  import('./components/DevLogin.js').then((m) => ({ default: m.DevLogin })),
);
const GestoriaLogin = lazy(() =>
  import('./components/GestoriaLogin.js').then((m) => ({ default: m.GestoriaLogin })),
);
// Presupuesto de entry (F2.3): el login dual y el portal completo solo se cargan
// cuando su ruta/estado los pide — fuera del chunk inicial del shell autenticado.
const LoginScreen = lazy(() =>
  import('./components/LoginScreen.js').then((m) => ({ default: m.LoginScreen })),
);
const PortalCliente = lazy(() =>
  import('./modules/portal-cliente/PortalCliente.js').then((m) => ({ default: m.PortalCliente })),
);
// Hub del lead (H0) · página pública os.munya.studio/lead/{slug}, SIN login, noindex.
// 4º uso del enlace mágico, misma SPA, fuera del chunk inicial del shell autenticado.
const HubPublic = lazy(() =>
  import('./modules/hub-public/HubPublic.js').then((m) => ({ default: m.HubPublic })),
);
// Propuesta a leads (M3 · #360) · página pública os.munya.studio/propuesta/{slug},
// SIN login, noindex, gate de clave. RUTA e IDENTIDAD propias (independiente del hub).
const PropuestaPublic = lazy(() =>
  import('./modules/propuesta-public/PropuestaPublic.js').then((m) => ({
    default: m.PropuestaPublic,
  })),
);

function FallbackShell({ children }: { children: ReactElement }): ReactElement {
  return <div className="py-2">{children}</div>;
}

export default function App(): ReactElement {
  const { user, loading } = useAuth();
  const path = usePathname(); // siempre llamado · React rules

  // C2 · dentro de la app iOS: registrar el token de push tras confirmar sesión (no-op en web)
  useEffect(() => {
    if (user) void initNativePush();
  }, [user]);

  // Portal cliente/constructora/logística · ruta pública independiente del Entra ID auth.
  // · #464 · /logistica es la puerta del tercer papel (PORTAL_HOME_PATH en el API). Las tres
  // rutas montan el MISMO armazón: quién entra lo decide el papel del acceso, no la URL —una
  // persona con dos obras y dos papeles no puede depender de por dónde entró.
  if (path.startsWith('/portal') || path.startsWith('/obra') || path.startsWith('/logistica')) {
    return (
      <Suspense fallback={<div className="fixed inset-0 bg-[var(--color-munya-cream)]" />}>
        <PortalCliente />
      </Suspense>
    );
  }

  // Hub del lead (H0) · ruta pública /lead/{slug}, SIN login, noindex. Misma SPA.
  if (path.startsWith('/lead/')) {
    return (
      <Suspense fallback={<div className="fixed inset-0 bg-[var(--color-munya-cream)]" />}>
        <HubPublic />
      </Suspense>
    );
  }

  // Propuesta a leads (M3 · #360) · ruta pública /propuesta/{slug}, SIN login, noindex,
  // gate de clave. Identidad propia, independiente del hub. Misma SPA.
  if (path.startsWith('/propuesta/')) {
    return (
      <Suspense fallback={<div className="fixed inset-0 bg-[var(--color-munya-cream)]" />}>
        <PropuestaPublic />
      </Suspense>
    );
  }

  // Acceso de desarrollador · ruta OCULTA (no enlazada), independiente del login Microsoft.
  if (path.startsWith('/acceso-dev')) {
    return (
      <Suspense
        fallback={
          <FallbackShell>
            <SkeletonCards />
          </FallbackShell>
        }
      >
        <DevLogin />
      </Suspense>
    );
  }

  // Acceso de gestoría externa · ruta dedicada (#46), independiente del login Microsoft.
  if (path.startsWith('/acceso-gestoria')) {
    return (
      <Suspense
        fallback={
          <FallbackShell>
            <SkeletonCards />
          </FallbackShell>
        }
      >
        <GestoriaLogin />
      </Suspense>
    );
  }

  // L4 · ruta mobile-first standalone para fichaje rápido vía PWA
  if (path.startsWith('/fichar')) {
    return (
      <Suspense fallback={<div className="fixed inset-0 bg-[var(--color-munya-cream)]" />}>
        <FichajeMobilePage />
      </Suspense>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[var(--color-munya-cream)] text-[var(--color-munya-coffee)] gap-4">
        <img src="/favicon.png" alt="" className="w-12 h-12 rounded-lg motion-safe:animate-pulse" />
        <p className="opacity-60 text-sm">Comprobando sesión…</p>
      </div>
    );
  }
  if (!user)
    return (
      <Suspense fallback={<div className="fixed inset-0 bg-[var(--color-munya-cream)]" />}>
        <LoginScreen />
      </Suspense>
    );

  // Diseño NO accede al CRM (board/contactos/archivo) — sí al Catálogo de servicios.
  const canSeeCrm = ['studio_admin', 'studio_director', 'developer'].includes(user.role);
  if (!canSeeCrm && path.startsWith('/crm') && !path.startsWith('/crm/catalogo-servicios')) {
    return (
      <AppShell title="Sin acceso">
        <p className="opacity-70 text-sm">No tienes permiso para acceder al CRM.</p>
      </AppShell>
    );
  }

  // Gestoría externa (#46): solo Inicio + Contabilidad. Cualquier otra ruta → Sin acceso.
  if (
    user.role === 'external_gestoria' &&
    !(path === '/' || path.startsWith('/contabilidad') || path.startsWith('/facturacion'))
  ) {
    return (
      <AppShell title="Sin acceso">
        <p className="opacity-70 text-sm">La gestoría solo tiene acceso a Inicio y Contabilidad.</p>
      </AppShell>
    );
  }

  // #54 · Blindaje rutas Contabilidad: solo Dirección + Gestión + developer + external_gestoria.
  // Diseño NO debe acceder ni por URL directa.
  const canSeeAccounting = [
    'studio_admin',
    'studio_director',
    'studio_management',
    'developer',
    'external_gestoria',
  ].includes(user.role);
  if (!canSeeAccounting && (path.startsWith('/contabilidad') || path.startsWith('/facturacion'))) {
    return (
      <AppShell title="Sin acceso">
        <p className="opacity-70 text-sm">No tienes permiso para acceder a Contabilidad.</p>
      </AppShell>
    );
  }

  // #54 · Blindaje rutas Administración: solo Dirección + developer.
  const canSeeAdmin = ['studio_admin', 'studio_director', 'developer'].includes(user.role);
  if (!canSeeAdmin && path.startsWith('/administracion')) {
    return (
      <AppShell title="Sin acceso">
        <p className="opacity-70 text-sm">No tienes permiso para acceder a Administración.</p>
      </AppShell>
    );
  }

  let page;
  if (path === '/web' || path.startsWith('/web/')) {
    if (!canAccessWebsite(user.role)) {
      return (
        <AppShell title="Sin acceso">
          <p className="opacity-70 text-sm">
            La gestión de la web está reservada a administradores.
          </p>
        </AppShell>
      );
    }
    page = (
      <AppShell>
        <Suspense
          fallback={
            <FallbackShell>
              <SkeletonCards count={6} />
            </FallbackShell>
          }
        >
          <WebsiteSpace />
        </Suspense>
      </AppShell>
    );
  } else if (path.startsWith('/calendario')) {
    page = (
      <AppShell>
        <Suspense
          fallback={
            <FallbackShell>
              <SkeletonCards count={4} />
            </FallbackShell>
          }
        >
          <CalendarioSpace />
        </Suspense>
      </AppShell>
    );
  } else if (path.startsWith('/proyectos')) {
    page = (
      <AppShell>
        <Suspense
          fallback={
            <FallbackShell>
              <SkeletonCards count={6} />
            </FallbackShell>
          }
        >
          <ProyectosSpace />
        </Suspense>
      </AppShell>
    );
  } else if (path.startsWith('/contabilidad') || path.startsWith('/facturacion')) {
    const subPath = path.replace(/^\/(contabilidad|facturacion)/, '');
    page = (
      <AppShell>
        <Suspense
          fallback={
            <FallbackShell>
              <SkeletonTable rows={6} cols={5} />
            </FallbackShell>
          }
        >
          <ContabilidadSpace subPath={subPath} />
        </Suspense>
      </AppShell>
    );
  } else if (path.startsWith('/crm/catalogo-servicios')) {
    page = (
      <AppShell>
        <Suspense
          fallback={
            <FallbackShell>
              <p className="opacity-50 p-6">Cargando catálogo…</p>
            </FallbackShell>
          }
        >
          <ServiceCatalogManager />
        </Suspense>
      </AppShell>
    );
  } else if (path.startsWith('/crm/contactos')) {
    page = (
      <AppShell>
        <Suspense
          fallback={
            <FallbackShell>
              <p className="opacity-50 p-6">Cargando contactos…</p>
            </FallbackShell>
          }
        >
          <ContactsManager />
        </Suspense>
      </AppShell>
    );
  } else if (path.startsWith('/crm/archivo')) {
    page = (
      <AppShell>
        <Suspense
          fallback={
            <FallbackShell>
              <p className="opacity-50 p-6">Cargando archivo…</p>
            </FallbackShell>
          }
        >
          <LeadArchive />
        </Suspense>
      </AppShell>
    );
  } else if (path.startsWith('/crm')) {
    page = (
      <AppShell>
        <Suspense
          fallback={
            <FallbackShell>
              <SkeletonKanban columns={4} cardsPerColumn={3} />
            </FallbackShell>
          }
        >
          <LeadBoard />
        </Suspense>
      </AppShell>
    );
  } else if (path.startsWith('/track')) {
    page = (
      <AppShell>
        <Suspense
          fallback={
            <FallbackShell>
              <SkeletonLeadList rows={8} />
            </FallbackShell>
          }
        >
          <TrackInboxSpace />
        </Suspense>
      </AppShell>
    );
  } else if (path.startsWith('/almacen')) {
    page = (
      <AppShell>
        <Suspense
          fallback={
            <FallbackShell>
              <SkeletonLeadList rows={8} />
            </FallbackShell>
          }
        >
          <AlmacenSpace />
        </Suspense>
      </AppShell>
    );
  } else if (path.startsWith('/administracion')) {
    page = (
      <AppShell>
        <Suspense
          fallback={
            <FallbackShell>
              <SkeletonTable rows={5} cols={4} />
            </FallbackShell>
          }
        >
          <AdministracionSpace />
        </Suspense>
      </AppShell>
    );
  } else {
    page = (
      <AppShell>
        <DashboardHome />
      </AppShell>
    );
  }

  return (
    <FeatureFlagsProvider>
      <ToastProvider>
        {page}
        <ChatGated />
        <Suspense fallback={null}>
          <CommandPalette />
        </Suspense>
        <OnboardingTour />
        <ShortcutsRoot />
        <Suspense fallback={null}>
          <FeedbackWidget />
        </Suspense>
        <NPSBanner />
      </ToastProvider>
    </FeatureFlagsProvider>
  );
}

// Chat sidebar respeta killswitch_chat y flag 'chat'
function ChatGated(): ReactElement | null {
  const killed = useKillswitch('chat');
  const enabled = useFeatureFlag('chat');
  if (killed || !enabled) return null;
  return (
    <div data-focus-hide>
      <Suspense fallback={null}>
        <ChatSidebar />
      </Suspense>
    </div>
  );
}
